import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  Grid,
  CircularProgress,
  Tooltip,
  IconButton,
  Collapse,
  Alert,
  Stack,
  Popper,
  ClickAwayListener,
  Paper as PopperPaper,
  MenuList,
  Grow,
  RadioGroup,
  Radio,
  FormControlLabel,OutlinedInput
} from '@mui/material';
import {
  Search as SearchIcon,
  Storefront as StorefrontIcon,
  Clear,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AccessTime as AccessTimeIcon,
  GridOn as GridOnIcon,
  BarChart as BarChartIcon,
  CalendarToday as CalendarIcon,
  Schedule as ScheduleIcon,
  HourglassBottom as HourglassBottomIcon,
  HourglassTop as HourglassTopIcon,
  Category,
  TableChart,
  PictureAsPdf,
  DeliveryDining as DeliveryIcon,
  TakeoutDining as TakeawayIcon,
  Restaurant as DineInIcon,
  ReceiptLong as TotalIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { getPeakHoursOrdersData, downloadExcelPeakHoursOrders, downloadPDFPeakHoursOrders, getOutletTimings } from '../services/reportService';
import outletService from '../services/outletService';

// All 24 hours for time filter
const allHours = Array.from({ length: 24 }, (_, i) =>
  String(i).padStart(2, '0') + ':00'
);

// Order type configurations
const ORDER_TYPES = {
  DELIVERY: {
    label: 'Delivery',
    icon: <DeliveryIcon fontSize="small" />,
    color: '#1976d2',
    bgColor: '#e3f2fd'
  },
  TAKEAWAY: {
    label: 'Takeaway',
    icon: <TakeawayIcon fontSize="small" />,
    color: '#2e7d32',
    bgColor: '#e8f5e9'
  },
  DINE_IN: {
    label: 'Dine In',
    icon: <DineInIcon fontSize="small" />,
    color: '#ed6c02',
    bgColor: '#fff3e0'
  },
  TOTAL: {
    label: 'Total Orders',
    icon: <TotalIcon fontSize="small" />,
    color: '#6c2eb5',
    bgColor: '#f3e5f5'
  }
};


const CustomTimeSelect = ({ label, value, onChange, placeholder, icon: Icon, timeIndex }) => {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleSelect = (hour) => {
    let indexHour = parseInt(hour.split(":")[0], 10);
    timeIndex(indexHour)
    onChange(hour);
    setOpen(false);
  };

  const handleListKeyDown = (event) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <Box>
      <FormControl fullWidth size="small" sx={{ mt: 1 }}>
        <InputLabel shrink={true}>{label}</InputLabel>
        <OutlinedInput
          ref={anchorRef}
          value={value || ''}
          placeholder={placeholder}
          onClick={handleToggle}
          readOnly
          label={label}
          startAdornment={Icon && (
            <InputAdornment position="start">
              <Icon fontSize="small" />
            </InputAdornment>
          )}
          notched={true}
        />
      </FormControl>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        style={{ zIndex: 9999 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
            }}
          >
            <PopperPaper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList
                  id="split-button-menu"
                  autoFocusItem={open}
                  onKeyDown={handleListKeyDown}
                  sx={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    width: anchorRef.current?.offsetWidth || 'auto'
                  }}
                >
                  <MenuItem
                    value=""
                    onClick={() => handleSelect('')}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Clear Selection
                  </MenuItem>
                  {allHours.map((hour) => (
                    <MenuItem
                      key={hour}
                      selected={value === hour}
                      onClick={() => handleSelect(hour)}
                    >
                      {hour}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </PopperPaper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};



function PeakHoursOrdersManagement() {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedOutlet, setSelectedOutlet] = useState('all');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [singleDate, setSingleDate] = useState(null)
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:00');
  const [selectedView, setSelectedView] = useState('grid');
  const [peakData, setPeakData] = useState({});
  const [timeSlots, setTimeSlots] = useState([]);
  const [orderTypes, setOrderTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outlets, setOutlets] = useState([]);
  const [dateFilter, setDateFilter] = useState('thisWeek');
  const [startTimeIndex, setStartTimeIndex] = useState(0);
  const [endTimeIndex, setEndTimeIndex] = useState(23);
  const [selectedOrderType, setSelectedOrderType] = useState('all');
  const [rowTotal, setRowTotal] = useState({
    "DELIVERY": 0,
    "TAKEAWAY": 0,
    "DINE_IN": 0,
    "TOTAL": 0
  })
  const [filterClear,setFilterClear] = useState(false)

  useEffect(() => {
    fetchPeakData();
    fetchOutlets();
      if(JSON.parse(localStorage.getItem('admin_outlet_id'))){
      setSelectedOutlet(JSON.parse(localStorage.getItem('admin_outlet_id')));
    }
  }, [selectedOutlet, startDate, endDate, singleDate, startTime, endTime, selectedOrderType,filterClear,dateFilter]);

    useEffect(() => {
    fetchOutletTiming();
  }, [selectedOutlet]);

  const fetchOutlets = async () => {
    try {
      const response = await outletService.getOutlets();
      setOutlets(response);
    } catch (error) {
      console.error('Error fetching outlets:', error);
    }
  };

    const formatLocalDate = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchPeakData = async () => {
    setLoading(true);
    // console.log("Single Date at Fetch Peak Date  => ", singleDate)
    let formattedDate;
    if (singleDate) {
      formattedDate = `${singleDate.getFullYear()}-${String(singleDate.getMonth() + 1).padStart(2, '0')
        }-${String(singleDate.getDate()).padStart(2, '0')}`;
    }

    try {
      const params = {
        outlet_id: selectedOutlet === 'all' ? null : selectedOutlet,
        date_filter: dateFilter !== 'all' && dateFilter !== "custom" ? dateFilter : null,
        start_date: dateFilter === "custom-multi" && startDate ? formatLocalDate(startDate) : null,
        end_date: dateFilter === "custom-multi" && endDate ? formatLocalDate(endDate) : null,
        single_date: dateFilter === "custom-single" && singleDate ? formatLocalDate(singleDate) : null,
        start_time_index: startTimeIndex,
        end_time_index: endTimeIndex
      };

      // Call API for orders peak hours data
      const response = await getPeakHoursOrdersData(params);
      // console.log("Orders Peak Response => ", response);

      if (response && response.data) {
        const { timeSlots: timeSlotList, orderTypes: orderTypeList, data: matrixData, rowTotals: row_totals } = response.data;

        setTimeSlots(timeSlotList || []);
        setOrderTypes(orderTypeList || []);
        setRowTotal(row_totals)
        // Format data for display
        const formattedData = {};
        if (orderTypeList && matrixData) {
          orderTypeList.forEach((type, index) => {
            formattedData[type] = {};
            if (timeSlotList) {
              timeSlotList.forEach((slot, slotIndex) => {
                formattedData[type][slot] = matrixData[index]?.[slotIndex] || 0;
              });
            }
          });
        }

        setPeakData(formattedData);
      }
    } catch (error) {
      console.error("Error fetching peak hours orders data:", error);
    } finally {
      setLoading(false);
    }
  };


  const fetchOutletTiming = async () => {
    try {
      const response = await getOutletTimings(selectedOutlet);
      // console.log(response)
      setStartTimeIndex(response.result.start_time_index)
      setEndTimeIndex(response.result.end_time_index)
      setStartTime(response.result.start_time)
      setEndTime(response.result.end_time)
    }
    catch (error) {
      console.error(error)
      setStartTimeIndex(0)
      setEndTimeIndex(23)
      setStartTime('00:00')
      setEndTime('23:00')
    }
  }

  // Get filtered time slots based on start and end time
  const getFilteredTimeSlots = () => {
    let filtered = timeSlots;
    if (startTime && endTime && timeSlots.length > 0) {
      const startIndex = timeSlots.indexOf(startTime);
      const endIndex = timeSlots.indexOf(endTime);
      if (startIndex !== -1 && endIndex !== -1) {
        filtered = timeSlots.slice(startIndex, endIndex + 1);
      }
    }
    return filtered;
  };

  const displayTimeSlots = getFilteredTimeSlots();

  // Get color based on value
  const getColorByValue = (value, maxValue) => {
    if (value === 0) return '#f5f5f5';
    const ratio = value / maxValue;
    if (ratio >= 0.8) return '#1b5e20';
    if (ratio >= 0.6) return '#2e7d32';
    if (ratio >= 0.4) return '#4caf50';
    if (ratio >= 0.2) return '#a5d6a7';
    return '#e8f5e9';
  };

  const getFontColorByValue = (value, maxValue) => {
    if (value === 0) return 'black';
    const ratio = value / maxValue;
    if (ratio >= 0.8) return 'white';
    if (ratio >= 0.6) return 'white';
    if (ratio >= 0.4) return 'white';
    if (ratio >= 0.2) return 'black';
    return 'black';
  };

  // Get heatmap color
  const getHeatmapColor = (value, maxValue) => {
    if (value === 0) return '#ffffff';
    const ratio = value / maxValue;
    if (ratio >= 0.8) return '#d32f2f';
    if (ratio >= 0.6) return '#f44336';
    if (ratio >= 0.4) return '#ff9800';
    if (ratio >= 0.2) return '#ffc107';
    return '#fff9c4';
  };

  // Get order types to display (filtered)
  const getDisplayOrderTypes = () => {
    if (selectedOrderType === 'all') {
      return orderTypes;
    }
    return orderTypes.filter(type => type === selectedOrderType);
  };

  const displayOrderTypes = getDisplayOrderTypes();

  // Calculate max value for color scaling
  const allValues = Object.values(peakData).flatMap(item =>
    Object.values(item)
  );
  const maxValue = Math.max(...allValues, 1);

  // Get row total
  const getRowTotal = (orderType) => {
    // console.log("Peak Data at Row Total => ", rowTotal)
    return rowTotal[orderType]
  };

  // Get peak hours for an order type
  const getPeakHours = (orderData) => {
    const entries = Object.entries(orderData);
    const sorted = entries.sort((a, b) => b[1] - a[1]);
    return sorted.slice(0, 3).map(([time, value]) => ({ time, value }));
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handler for date filter change
  const handleDateFilterChange = (value) => {
    setDateFilter(value);

    const today = new Date();
    const currentDate = new Date();

    switch (value) {
      case 'all':
        setStartDate(null);
        setEndDate(null);
        break;
      case 'today':
        setStartDate(new Date(today.setHours(0, 0, 0, 0)));
        setEndDate(new Date(today.setHours(23, 59, 59, 999)));
        break;
      case 'thisWeek':
        const firstDayOfWeek = new Date(today);
        const diff = today.getDay() === 0 ? 6 : today.getDay() - 1;
        firstDayOfWeek.setDate(today.getDate() - diff);
        firstDayOfWeek.setHours(0, 0, 0, 0);

        const lastDayOfWeek = new Date(firstDayOfWeek);
        lastDayOfWeek.setDate(firstDayOfWeek.getDate() + 6);
        lastDayOfWeek.setHours(23, 59, 59, 999);

        setStartDate(firstDayOfWeek);
        setEndDate(lastDayOfWeek);
        break;
      case 'thisMonth':
        const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        firstDayOfMonth.setHours(0, 0, 0, 0);

        const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        lastDayOfMonth.setHours(23, 59, 59, 999);

        setStartDate(firstDayOfMonth);
        setEndDate(lastDayOfMonth);
        break;
      case 'custom':
        break;
      default:
        break;
    }
  };

  const handleDownloadExcel = async () => {
    try {
      await downloadExcelPeakHoursOrders(
        selectedOutlet,
        selectedOrderType.toLowerCase(),
        formatLocalDate(startDate),
        formatLocalDate(endDate),
        startTimeIndex,
        endTimeIndex,
        formatLocalDate(singleDate),
        
      );
    } catch (error) {
      console.error("Error downloading Excel:", error);
    }
  };

  const handleExportPDF = async () => {
    // console.log("Yes Here")
    try {
      await downloadPDFPeakHoursOrders(
        selectedOutlet,
        selectedOrderType.toLowerCase(),
        formatLocalDate(startDate),
        formatLocalDate(endDate),
        startTimeIndex,
        endTimeIndex,
        formatLocalDate(singleDate)
      );
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  // Clear filters handler
  const handleClearFilters = () => {
    setSelectedOutlet('all');
    setDateFilter('all');
    setStartDate(null);
    setEndDate(null);
    setStartTime('00:00');
    setEndTime('23:00');
    setSelectedOrderType('all');
    setStartTimeIndex(0)
    setEndTimeIndex(23)
    setPage(0);
    setSingleDate(null)
    setFilterClear(!filterClear)
  };

  // Render Table View
  const renderTableView = () => (
    <TableContainer sx={{ maxHeight: 600, borderRadius: '6px' }}>
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                minWidth: 150,
                fontWeight: 'bold',
                position: 'sticky',
                left: 0,
                bgcolor: '#f5f5f5',
                zIndex: 3
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold">Order Type</Typography>
            </TableCell>
            {displayTimeSlots.map(slot => (
              <TableCell
                key={slot}
                align="center"
                sx={{
                  minWidth: 55,
                  fontSize: '10px',
                  padding: '4px',
                  bgcolor: '#f5f5f5'
                }}
              >
                {slot}
              </TableCell>
            ))}
            <TableCell
              align="center"
              sx={{
                minWidth: 70,
                fontWeight: 'bold',
                bgcolor: '#f5f5f5'
              }}
            >
              Total
            </TableCell>
            {/* <TableCell 
              align="center"
              sx={{ 
                minWidth: 120,
                fontWeight: 'bold',
                bgcolor: '#f5f5f5'
              }}
            >
              Peak Hours
            </TableCell> */}
          </TableRow>
        </TableHead>
        <TableBody>
          {displayOrderTypes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={displayTimeSlots.length + 3} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">No data found</Typography>
              </TableCell>
            </TableRow>
          ) : (
            displayOrderTypes.map((orderType) => {
              const orderData = peakData[orderType] || {};
              const total = getRowTotal(orderType);
              const peaks = getPeakHours(orderData);
              const orderConfig = ORDER_TYPES[orderType];

              return (
                <TableRow key={orderType} hover>
                  <TableCell
                    sx={{
                      fontWeight: 500,
                      position: 'sticky',
                      left: 0,
                      bgcolor: 'white',
                      zIndex: 2
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {orderConfig && (
                        <Box sx={{ color: orderConfig.color }}>
                          {orderConfig.icon}
                        </Box>
                      )}
                      {orderConfig ? orderConfig.label : orderType}
                    </Box>
                  </TableCell>

                  {displayTimeSlots.map(slot => {
                    const value = orderData[slot] || 0;
                    const bgColor = selectedView === 'heatmap'
                      ? getHeatmapColor(value, maxValue)
                      : getColorByValue(value, maxValue);

                    return (
                      <TableCell
                        key={slot}
                        align="center"
                        sx={{
                          bgcolor: bgColor,
                          padding: '4px',
                          fontWeight: value > 0 ? '500' : 'normal',
                          color: getFontColorByValue(value,maxValue)
                        }}
                      >
                        <Tooltip title={`${orderType}: ${slot} - ${value} orders`}>
                          <span>{value}</span>
                        </Tooltip>
                      </TableCell>
                    );
                  })}

                  <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                    {total}
                  </TableCell>

                  {/* <TableCell align="center">
                    <Box>
                      {peaks.map((peak, idx) => (
                        <Chip
                          key={idx}
                          label={`${peak.time} (${peak.value})`}
                          size="small"
                          color={idx === 0 ? 'error' : idx === 1 ? 'warning' : 'info'}
                          variant="outlined"
                          sx={{ m: 0.5, fontSize: '9px' }}
                        />
                      ))}
                    </Box>
                  </TableCell> */}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ width: '100%', p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              Orders Peak Hours Management
            </Typography>
            <Typography color="text.secondary" variant="body1">
              Monitor order distribution across 24-hour time slots by order type
            </Typography>
          </Box>
          <Box>
            <Button variant='contained' sx={{ mr: 1 }} onClick={handleDownloadExcel} startIcon={<TableChart />}>
              Download Excel
            </Button>
            <Button variant='contained' onClick={handleExportPDF} startIcon={<PictureAsPdf />}>
              Download PDF
            </Button>
          </Box>
        </Box>

        {/* Filters Section */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            {/* Outlet Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Outlet</InputLabel>
                <Select
                  value={selectedOutlet}
                  disabled={JSON.parse(localStorage.getItem('admin_outlet_id'))}
                  label="Outlet"
                  onChange={(e) => {
                    setSelectedOutlet(e.target.value)
                    if (e.target.value) {
                      setStartTimeIndex(0)
                      setEndTimeIndex(23)
                      setStartTime('00:00')
                      setEndTime('23:00')
                    }

                  }}
                  startAdornment={
                    <InputAdornment position="start">
                      <StorefrontIcon fontSize="small" />
                    </InputAdornment>
                  }
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  <MenuItem value="all">All Outlets</MenuItem>
                  {outlets.map((outlet) => (
                    <MenuItem key={outlet.id} value={outlet.id}>
                      {outlet.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Order Type Filter */}
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Order Type</InputLabel>
                <Select
                  value={selectedOrderType}
                  label="Order Type"
                  onChange={(e) => setSelectedOrderType(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <AccessTimeIcon fontSize="small" />
                    </InputAdornment>
                  }
                  MenuProps={{
                    disableScrollLock: true,
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  }}
                >
                  <MenuItem value="all">All Types</MenuItem>
                  {orderTypes.map((type) => {
                    const config = ORDER_TYPES[type];
                    return (
                      <MenuItem key={type} value={type}>
                        <Box display="flex" alignItems="center" gap={1}>
                          {config && config.icon}
                          {config ? config.label : type}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Grid>

            {/* Start Time */}
            <Grid item xs={12} sm={6} md={1.5}>
              <CustomTimeSelect
                label="Start Time"
                value={startTime}
                onChange={setStartTime}
                placeholder="00:00"
                icon={HourglassBottomIcon}
                timeIndex={setStartTimeIndex}
              />
            </Grid>

            {/* End Time */}
            <Grid item xs={12} sm={6} md={1.5}>
              <CustomTimeSelect
                label="End Time"
                value={endTime}
                onChange={setEndTime}
                placeholder="23:00"
                icon={HourglassTopIcon}
                timeIndex={setEndTimeIndex}
              />
            </Grid>

            {/* View Type Toggle */}
            {/* <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>View Type</InputLabel>
                <Select
                  value={selectedView}
                  label="View Type"
                  onChange={(e) => setSelectedView(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <BarChartIcon fontSize="small" />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="grid">Grid View</MenuItem>
                  <MenuItem value="heatmap">Heatmap View</MenuItem>
                </Select>
              </FormControl>
            </Grid> */}

            {/* Action Buttons */}
            <Grid item xs={12} md={1}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<Clear />}
                onClick={handleClearFilters}
                size="small"
              >
                Clear
              </Button>
            </Grid>
          </Grid>

          {/* Date Filter Section */}
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: '180px', mt: 2 }}>
              <InputLabel sx={{ mt: 0.5 }}>Date Filter</InputLabel>
              <Select
                value={dateFilter}
                label="Date Filter"
                onChange={(e) => handleDateFilterChange(e.target.value)}
                startAdornment={
                  <InputAdornment position="start">
                    <CalendarIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="thisWeek">This Week</MenuItem>
                <MenuItem value="thisMonth">This Month</MenuItem>
                <MenuItem value="custom-multi">Custom (Multiple Dates)</MenuItem>
                <MenuItem value="custom-single">Custom (Single Date)</MenuItem>
              </Select>
            </FormControl>

            {dateFilter === 'custom-multi' && (
              <>
                <DatePicker
                  sx={{ mt: 1 }}
                  label="Start Date"
                  value={startDate}
                  onChange={(newValue) => {
                    setStartDate(newValue);
                    // If end date is before new start date, clear it
                    if (endDate && newValue && newValue > endDate) {
                      setEndDate(null);
                    }
                  }}
                  format="dd-MM-yyyy"
                  maxDate={endDate || undefined} // Prevent selecting after end date
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: '180px', mt: 1.5 },
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }
                    }
                  }}
                />
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  format="dd-MM-yyyy"
                  minDate={startDate || undefined} // Prevent selecting before start date
                  disabled={!startDate} // Disable if no start date selected
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: '180px', mt: 1.5 },
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }
                    }
                  }}
                />
              </>
            )}


            {dateFilter === 'custom-single' && (
              <>
                <DatePicker
                  sx={{ mt: 1 }}
                  label="Select Date"
                  value={singleDate}
                  onChange={(newValue) => {
                    const value = newValue
                    setSingleDate(value);
                  }}
                  format="dd-MM-yyyy"
                  maxDate={new Date()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: '180px', mt: 1.5 },
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }
                    }
                  }}
                />
              </>
            )}
          </Box>
        </Paper>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Time Range:</strong> {startTime} - {endTime} |
            <strong> Order Types:</strong> {displayOrderTypes.length} displayed |
            <strong> Time Slots:</strong> {displayTimeSlots.length} displayed
          </Typography>
        </Alert>

        {/* Summary Cards */}
        {/* <Grid container spacing={2} sx={{ mb: 3 }}>
          {orderTypes.map((type) => {
            const total = getRowTotal(type);
            const config = ORDER_TYPES[type];
            return (
              <Grid item xs={12} sm={6} md={3} key={type}>
                <Paper sx={{ p: 2, bgcolor: config?.bgColor || '#f5f5f5' }}>
                  <Box display="flex" alignItems="center" gap={1}>
                    {config && config.icon}
                    <Typography variant="subtitle2" color="text.secondary">
                      {config ? config.label : type}
                    </Typography>
                  </Box>
                  <Typography variant="h5" fontWeight="bold">
                    {total}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Total Orders
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid> */}

        {/* Main Table */}
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          {renderTableView()}

          {/* <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={displayOrderTypes.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} of ${count}`}
          /> */}
        </Paper>

        {/* Footer Stats */}
        {/* <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Total Order Types: {orderTypes.length} | Displaying: {displayOrderTypes.length} types
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Time Range: {startTime} - {endTime}
            {selectedOutlet !== 'all' && ` | Outlet: ${outlets.find(o => o.id === selectedOutlet)?.name}`}
            {startDate && ` | From: ${startDate.toLocaleDateString()}`}
            {endDate && ` | To: ${endDate.toLocaleDateString()}`}
          </Typography>
        </Box> */}
      </Box>
    </LocalizationProvider>
  );
}

export default PeakHoursOrdersManagement;